// SPDX-License-Identifier: MIT
/** Shared helpers for library scenes: scene length, logo boxes, CJK-aware type fitting. */
import { type ResolvedBrand, font } from '../brand.ts';
import type { Box } from '../kit/path.ts';
import type { SceneEnv } from '../scene.ts';
/** Length of the current scene in beats. Library scenes scale their choreography to it. */
export const sceneBeats = (env: Pick<SceneEnv, 'dur' | 'spb'>): number => Math.round((env.dur / env.spb) * 1000) / 1000;
/** Code-point ranges of CJK ideographs, kana, hangul and fullwidth forms. */
const CJK_RANGES: Array<[
    number,
    number
]> = [
    [0x2e80, 0x9fff],
    [0xac00, 0xd7af],
    [0xf900, 0xfaff],
    [0xff00, 0xffef],
    [0x3000, 0x303f],
];
export const hasCJK = (text: string): boolean => Array.from(text).some((c) => {
    const cp = c.codePointAt(0) ?? 0;
    return CJK_RANGES.some(([a, b]) => cp >= a && cp <= b);
});
/** Tracking for display type: tight for Latin, none for CJK (it has no word spaces to lose). */
export const displayTracking = (text: string, px: number): string => (hasCJK(text) ? '0px' : `${-px / 50}px`);
/** True when the logo already contains the brand name (so no separate wordmark is set). */
export function logoHasWordmark(brand: ResolvedBrand): boolean {
    return !!brand.logo?.parts.some((p) => /word|name|text/i.test(p.role ?? ''));
}
/** A box for the mark centred on (cx, cy), within maxW × maxH, keeping the logo's aspect. */
export function markBox(brand: ResolvedBrand, cx: number, cy: number, maxW: number, maxH: number): Box {
    const b = brand.logo?.bounds ?? { x: 0, y: 0, w: 1, h: 1 };
    const aspect = b.w / Math.max(1e-6, b.h);
    const w = Math.min(maxW, maxH * aspect);
    const h = w / aspect;
    return { x: cx - w / 2, y: cy - h / 2, w, h };
}
/**
 * Set `ctx.font` to the largest size ≤ maxPx at which `text` fits `maxWidth` on one line (with
 * display tracking applied), and return that size.
 */
export function fitLine(ctx: CanvasRenderingContext2D, brand: ResolvedBrand, text: string, weight: number, maxPx: number, maxWidth: number, minPx = 28): number {
    let px = maxPx;
    for (let i = 0; i < 24; i++) {
        ctx.font = font(brand, weight, px);
        ctx.letterSpacing = displayTracking(text, px);
        if (ctx.measureText(text).width <= maxWidth || px <= minPx)
            break;
        px = Math.max(minPx, px * 0.94);
    }
    ctx.letterSpacing = '0px';
    return px;
}
